import React from 'react';
import { LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import {
  FiFileText, FiCheckCircle, FiClock, FiXCircle,
  FiAlertTriangle, FiArchive,
} from 'react-icons/fi';
import StatTile from '../shared/StatTile';

const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

// Thin wrapper that keeps the drill-down click behavior while the tile
// visuals live in the shared StatTile.
const ClickableTile = ({ onClick, children }) => {
  if (!onClick) return <div className="tw-h-full">{children}</div>;
  return (
    <div
      onClick={onClick}
      className="tw-h-full tw-rounded tw-cursor-pointer tw-transition-all hover:tw-shadow-md hover:-tw-translate-y-0.5"
    >
      {children}
    </div>
  );
};
ClickableTile.propTypes = { onClick: PropTypes.func, children: PropTypes.node };

// Waiver status ids (Constants::CRM_WAIVER_STATUS_*)
const STATUS = { SUBMITTED: 1, WAITING: 2, APPROVED: 3, REJECTED: 4, CANCELED: 5 };

// Each KPI drills down to the matching record subset via
// onDrillDown(title, extraParams) — params mirror the backend filter_base.
const WaiverSummaryCards = ({ data, loading, onDrillDown }) => {
  if (loading && !data) {
    return (
      <div className="tw-bg-white tw-rounded tw-border tw-border-gray-100 tw-flex tw-justify-center tw-py-10">
        <LoadingSpinner size="md" />
      </div>
    );
  }
  if (!data) return null;

  const drill = (title, extraParams) =>
    onDrillDown ? () => onDrillDown(title, extraParams) : undefined;

  return (
    <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 xl:tw-grid-cols-6 tw-gap-3">
      <ClickableTile onClick={drill('All Waivers', {})}>
        <StatTile label="Total Waivers" value={fmt(data.total)}
          sub={`${fmtM(data.total_value)} value`}
          icon={FiFileText} theme="indigo" />
      </ClickableTile>
      <ClickableTile onClick={drill('Approved Waivers', { statuses: [STATUS.APPROVED] })}>
        <StatTile label="Approved" value={fmt(data.approved)}
          sub={`${fmt(data.closed)} closed`}
          icon={FiCheckCircle} theme="green" />
      </ClickableTile>
      <ClickableTile onClick={drill('Pending Approval', { statuses: [STATUS.SUBMITTED, STATUS.WAITING] })}>
        <StatTile label="Pending Approval" value={fmt((data.submitted || 0) + (data.waiting || 0))}
          sub={`${fmt(data.submitted)} submitted · ${fmt(data.waiting)} waiting`}
          icon={FiClock} theme="amber" />
      </ClickableTile>
      <ClickableTile onClick={drill('Rejected / Canceled Waivers', { statuses: [STATUS.REJECTED, STATUS.CANCELED] })}>
        <StatTile label="Rejected / Canceled" value={fmt((data.rejected || 0) + (data.canceled || 0))}
          sub={`${fmt(data.rejected)} rejected · ${fmt(data.canceled)} canceled`}
          icon={FiXCircle} theme="rose" />
      </ClickableTile>
      <ClickableTile onClick={drill('Overdue Waivers', { overdue: 'true' })}>
        <StatTile label="Overdue" value={fmt(data.overdue)}
          sub="Approved, past commitment"
          icon={FiAlertTriangle} theme="amber" />
      </ClickableTile>
      <ClickableTile onClick={drill('Pending Close Waivers', { statuses: [STATUS.APPROVED], closed: 'false' })}>
        <StatTile label="Pending Close" value={fmt(data.pending_close)}
          sub="Approved, not closed"
          icon={FiArchive} theme="cyan" />
      </ClickableTile>
    </div>
  );
};

WaiverSummaryCards.propTypes = {
  data: PropTypes.object,
  loading: PropTypes.bool,
  onDrillDown: PropTypes.func,
};
WaiverSummaryCards.defaultProps = { data: null, loading: false, onDrillDown: null };

export default WaiverSummaryCards;
